import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import RankBadge from "../../components/club/RankBadge";
import { ClubApi } from "../../services/clubApi";
import { buildCasePath } from "../../utils/clubCasePath";

const RANK_LABELS: Record<string, string> = {
  junior_detective: "Junior Detective",
  detective: "Detective",
  senior_detective: "Senior Detective",
  lead_detective: "Lead Detective",
  special_investigator: "Special Investigator",
};

const RANK_STEPS = [
  { key: "junior_detective", minMonths: 0 },
  { key: "detective", minMonths: 3 },
  { key: "senior_detective", minMonths: 6 },
  { key: "lead_detective", minMonths: 9 },
  { key: "special_investigator", minMonths: 12 },
];

const getNextRank = (paidMonths = 0) => {
  const current = Number(paidMonths || 0);
  const next = RANK_STEPS.find((step) => step.minMonths > current);
  if (!next) return null;
  return {
    label: RANK_LABELS[next.key],
    monthsRemaining: next.minMonths - current,
  };
};

const toEmbedUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (value.includes("/embed/")) return value;

  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch?.[1]) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  const shortMatch = value.match(/youtu\.be\/([^?]+)/);
  if (shortMatch?.[1]) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  return value;
};

const ClubPaidMemberLandingPreviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState<any>(null);
  const [cases, setCases] = useState<any[]>([]);
  const [usingPreviewData, setUsingPreviewData] = useState(false);

  const welcomeVideoUrl = toEmbedUrl(import.meta.env.VITE_CLUB_WELCOME_VIDEO_URL || "");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [memberResponse, entitlementResponse] = await Promise.all([
          ClubApi.getMe(),
          ClubApi.getEntitlements(),
        ]);

        setMember(memberResponse.member);
        setCases(entitlementResponse.cases || []);
        setUsingPreviewData(false);
      } catch (err: any) {
        setMember({
          displayName: "Junior Detective",
          membershipNumber: "CCM-000001",
          rank: "junior_detective",
          paidMonthsCount: 1,
          status: "active",
        });
        setCases([
          { caseIndex: 1, title: "The Midnight Ledger" },
          { caseIndex: 2, title: "Harborline Cipher" },
        ]);
        setUsingPreviewData(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const nextRank = useMemo(() => getNextRank(member?.paidMonthsCount), [member?.paidMonthsCount]);
  const sortedCases = useMemo(
    () => [...cases].sort((a, b) => Number(b?.caseIndex || 0) - Number(a?.caseIndex || 0)),
    [cases]
  );

  if (loading) return <div className="p-6 text-white">Loading member preview...</div>;

  return (
    <section className="mx-auto max-w-6xl px-4 pb-8 pt-3 text-white">
      {usingPreviewData && (
        <div className="mb-4 rounded-xl border border-amber-300/30 bg-amber-500/10 p-3 text-sm text-amber-100">
          Showing preview data because live club member data is unavailable.
        </div>
      )}
      <div className="relative overflow-hidden rounded-[28px] border border-cyan-300/25 bg-gradient-to-br from-[#07101f] via-[#101824] to-[#2a120f] p-8 shadow-[0_28px_80px_rgba(6,30,60,0.45)]">
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-amber-500/20 blur-3xl" />

        <p className="text-xs uppercase tracking-[0.3em] text-cyan-200">Member Briefing</p>
        <h1 className="mt-3 text-4xl font-black leading-tight md:text-5xl">
          Welcome Back, {member?.displayName || "Detective"}
        </h1>
        <p className="mt-4 max-w-3xl text-zinc-200">
          Your monthly case pipeline is active. Review your status, watch the mission briefing,
          and continue your club progression.
        </p>

        <div className="mt-7 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Membership</p>
            <p className="mt-2 text-sm font-semibold">{member?.membershipNumber}</p>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Current Rank</p>
            <div className="mt-2 flex items-center gap-3">
              <RankBadge
                rankKey={String(member?.rank || "")}
                rankLabel={RANK_LABELS[member?.rank] || "Junior Detective"}
                size={40}
                className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 p-1"
              />
              <p className="text-sm font-semibold">
                {RANK_LABELS[member?.rank] || "Junior Detective"}
              </p>
            </div>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Paid Months</p>
            <p className="mt-2 text-sm font-semibold">{member?.paidMonthsCount || 0}</p>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Status</p>
            <p className="mt-2 text-sm font-semibold capitalize">{member?.status || "active"}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-2xl border border-cyan-200/20 bg-zinc-950/70 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">Welcome Briefing</h2>
            <span className="rounded-full border border-cyan-300/40 px-3 py-1 text-xs text-cyan-200">
              Optional Video
            </span>
          </div>

          {welcomeVideoUrl ? (
            <div className="overflow-hidden rounded-xl border border-white/10">
              <iframe
                title="Club welcome briefing"
                src={welcomeVideoUrl}
                className="aspect-video w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-white/30 bg-white/5 p-6 text-sm text-zinc-300">
              Add `VITE_CLUB_WELCOME_VIDEO_URL` to show your member welcome video here.
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/agency/dashboard"
              className="rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#0b1322]"
            >
              Open Agency Dashboard
            </Link>
            <Link
              to="/agency/settings"
              className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold"
            >
              Fulfillment Settings
            </Link>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-amber-300/25 bg-amber-500/10 p-5">
            <h3 className="text-lg font-bold text-amber-100">Progression Signal</h3>
            {nextRank ? (
              <p className="mt-2 text-sm text-amber-50">
                {nextRank.monthsRemaining} month
                {nextRank.monthsRemaining === 1 ? "" : "s"} until {nextRank.label}.
              </p>
            ) : (
              <p className="mt-2 text-sm text-amber-50">Top rank achieved. Keep solving.</p>
            )}
          </div>

          <div className="rounded-2xl border border-white/15 bg-zinc-950/70 p-5">
            <h3 className="text-lg font-bold">Accessible Cases</h3>
            {sortedCases.length === 0 ? (
              <p className="mt-3 text-sm text-zinc-300">No granted cases yet.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {sortedCases.slice(0, 5).map((clubCase) => (
                  <li
                    key={clubCase.caseIndex}
                    className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                  >
                    <span className="text-sm">
                      Case #{clubCase.caseIndex} - {clubCase.title}
                    </span>
                    <Link
                      to={buildCasePath(
                        clubCase.caseIndex,
                        clubCase.title,
                        clubCase.briefingTitle
                      )}
                      className="rounded bg-zinc-800 px-2.5 py-1 text-xs font-semibold"
                    >
                      Open
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClubPaidMemberLandingPreviewPage;
